import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ArcElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';

@Component({
    selector: 'app-card-chart',
    template: `
    <div
      class="tw-w-full app-blue-container tw-flex tw-flex-col tw-items-center app-pt3 app-pb3"
      >
      <div class="chart">
        <canvas #chartCanvas role="img"></canvas>
      </div>
      <div
        class="tw-flex tw-flex-col tw-font-bold tw-tracking-wide app-color-slate tw-uppercase tw-text-[13px] app-pt2"
        >
        @for (series of config.series; track series) {
          <div
            class="tw-flex tw-items-center"
            >
            <div class="tw-flex-none app-pr2 tw-mt-1">
              <mat-icon class="legend-icon" [style.color]="series.color"
                >circle</mat-icon
                >
              </div>
              <div class="tw-flex-grow">
                {{ series.value }} - {{ series.label | translation: translationRequest }}
              </div>
            </div>
          }
        </div>
      </div>
    `,
    styles: [
        `
      .chart {
        height: 150px;
      }

      .legend-icon {
        width: 13px;
        height: 13px;
        font-size: 13px;
      }
    `,
    ],
    standalone: false
})
export class CardChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input()
  config: ChartConfig;

  @Input()
  translationRequest: TranslationRequest;

  @ViewChild('chartCanvas') canvasRef: ElementRef<HTMLCanvasElement>;

  private chart: Chart | null = null;

  private static chartRegistered = false;

  private registerChart(): void {
    if (!CardChartComponent.chartRegistered) {
      Chart.register(
        DoughnutController,
        CategoryScale,
        LinearScale,
        ArcElement,
        Legend,
        Title,
        Tooltip
      );
      CardChartComponent.chartRegistered = true;
    }
  }

  private createChart(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.config?.series?.length || this.chart) {
      return;
    }
    this.registerChart();
    const labels = this.config.series.map((x) => x.label);
    const values = this.config.series.map((x) => x.value);
    const colors = this.config.series.map((x) => x.color);
    this.chart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: '#000000',
            borderWidth: 1,
            borderAlign: 'center',
          },
        ],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        cutout: 48,
        elements: {
          arc: {
            borderWidth: 0,
          },
        },
        plugins: {
          legend: {
            display: false,
            position: 'bottom',
            labels: {
              font: {
                size: 14,
                weight: 'bold',
                family: '"Source Sans Pro", "Helvetica Neue", sans-serif',
              },
            },
          },
          title: {
            display: false,
            text: '',
          },
        },
      },
    });
  }

  private updateChartData(): void {
    if (!this.chart || !this.config?.series?.length) {
      return;
    }
    const labels = this.config.series.map((x) => x.label);
    const values = this.config.series.map((x) => x.value);
    const colors = this.config.series.map((x) => x.color);
    this.chart.data.labels = labels;
    this.chart.data.datasets[0] = {
      data: values,
      backgroundColor: colors,
      borderColor: '#000000',
      borderWidth: 1,
      borderAlign: 'center',
    };
    this.chart.update('none');
  }

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      if (this.chart && this.config) {
        this.updateChartData();
      } else if (this.config && !this.chart) {
        // Canvas may not be in DOM yet (e.g. inside *ngIf); defer so view can render
        setTimeout(() => this.createChart(), 0);
      }
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.chart = null;
  }
}

export class ChartConfig {
  series: ChartSeries[] = [];

  addSeries(label: string, value: number, color: string): ChartConfig {
    this.series.push(new ChartSeries(label, value, color));
    return this;
  }
}

export class ChartSeries {
  constructor(
    public label: string,
    public value: number,
    public color: string
  ) {}
}
